import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  XMarkIcon,
  SparklesIcon,
  CheckIcon,
  BoltIcon,
  ChartBarIcon,
  DocumentTextIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from './LoadingSpinner';
import { useLanguageStore } from '../../stores/languageStore';

interface PremiumUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: () => Promise<void>;
  isUpgrading?: boolean;
}

const PremiumUpgradeModal: React.FC<PremiumUpgradeModalProps> = ({
  isOpen,
  onClose,
  onUpgrade,
  isUpgrading = false
}) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguageStore();

  if (!isOpen) return null;

  const premiumBenefits = [
    {
      icon: BoltIcon,
      title: t('premium.benefits.advancedAI.title', 'Advanced AI Models'),
      description: t('premium.benefits.advancedAI.description', 'Access to the most sophisticated legal AI models for superior analysis')
    },
    {
      icon: ChartBarIcon,
      title: t('premium.benefits.deeperAnalysis.title', 'Deeper Case Analysis'),
      description: t('premium.benefits.deeperAnalysis.description', 'Comprehensive legal precedent research and case strength evaluation')
    },
    {
      icon: DocumentTextIcon,
      title: t('premium.benefits.enhancedDocs.title', 'Enhanced Documentation'),
      description: t('premium.benefits.enhancedDocs.description', 'Professional-grade legal documents with advanced formatting and structure')
    },
    {
      icon: ClockIcon,
      title: t('premium.benefits.prioritySupport.title', 'Priority Processing'),
      description: t('premium.benefits.prioritySupport.description', 'Faster analysis and priority queue for all legal services')
    }
  ];

  const handleUpgrade = async () => {
    try {
      await onUpgrade();
    } catch (error) {
      console.error('Premium upgrade failed:', error);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className={`inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6 ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>
          {/* Loading Overlay */}
          {isUpgrading && (
            <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-10">
              <div className="text-center">
                <LoadingSpinner size="lg" />
                <p className="mt-4 text-sm font-medium text-gray-900">
                  {t('premium.upgrading', 'Upgrading to Premium...')}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  {t('premium.upgradingDescription', 'Please wait while we enhance your case with premium AI analysis')}
                </p>
              </div>
            </div>
          )}

          {/* Header */}
          <div className={`flex items-center justify-between mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="flex-shrink-0">
                <SparklesIcon className="h-8 w-8 text-yellow-500" />
              </div>
              <div className={`${isRTL ? 'mr-3' : 'ml-3'}`}>
                <h3 className="text-lg font-medium text-gray-900">
                  {t('premium.modal.title', 'Upgrade to Premium')}
                </h3>
                <p className="text-sm text-gray-500">
                  {t('premium.modal.subtitle', 'Unlock advanced AI capabilities')}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={isUpgrading}
              className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Free Upgrade Banner */}
          <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
            <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
              <CheckIcon className={`h-5 w-5 text-green-500 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              <span className="text-sm font-medium text-green-800">
                {t('premium.freeUpgrade', 'Limited Time: Free Premium Upgrade!')}
              </span>
            </div>
            <p className="mt-1 text-xs text-green-700">
              {t('premium.freeUpgradeDescription', 'Experience premium features at no cost - upgrade now to enhance your case analysis.')}
            </p>
          </div>

          {/* Benefits */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-900 mb-4">
              {t('premium.benefits.title', 'Premium Benefits Include:')}
            </h4>
            <div className="space-y-4">
              {premiumBenefits.map((benefit, index) => (
                <div key={index} className={`flex items-start ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="flex-shrink-0">
                    <benefit.icon className="h-5 w-5 text-cyan-500 mt-0.5" />
                  </div>
                  <div className={`${isRTL ? 'mr-3' : 'ml-3'}`}>
                    <p className="text-sm font-medium text-gray-900">
                      {benefit.title}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className={`flex ${isRTL ? 'space-x-reverse space-x-3 flex-row-reverse' : 'space-x-3'}`}>
            <button
              onClick={onClose}
              disabled={isUpgrading}
              className="flex-1 inline-flex justify-center px-4 py-3 border border-gray-300 shadow-sm text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 min-h-[48px] transition-all"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              onClick={handleUpgrade}
              disabled={isUpgrading}
              className="flex-1 inline-flex justify-center items-center px-4 py-3 border border-transparent shadow-sm text-base font-medium rounded-lg text-white bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 min-h-[48px] transition-all"
            >
              {isUpgrading ? (
                <>
                  <LoadingSpinner size="sm" className={`${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {t('premium.upgrading', 'Upgrading...')}
                </>
              ) : (
                <>
                  <SparklesIcon className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {t('premium.upgradeNow', 'Upgrade to Premium')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumUpgradeModal;